import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { SEOHead } from '../components/SEOHead'
import { useAuth } from '@/core/AuthContext'

/**
 * LoginPage component - Main entry point for user authentication
 * 
 * @component
 * @description
 * Primary login page for the Start Grounded application.
 * Provides phone number authentication for clients and displays 
 * branding/messaging for new users.
 * 
 * Features:
 * - Phone number authentication via /phone-login redirect
 * - Automatic role-based redirect for authenticated users
 * - Clean, branded UI with motion animations
 * - Responsive design with centered card layout
 * - Framer Motion animations for smooth entrance effects
 * 
 * User Flows:
 * - Unauthenticated users: See login prompt, click to phone auth
 * - Authenticated trainers: Auto-redirect to /trainer dashboard
 * - Authenticated clients: Auto-redirect to /client dashboard
 * 
 * Authentication Methods:
 * - Phone number (primary) - redirects to /phone-login
 * - Secret links for trainers - /t/:token
 * - Secret links for test clients - /c/:token
 * 
 * @note Trainers use secret access links (/t/:token) instead of this page
 * @note Test/dev login options moved to /debug route (see GROU-050)
 * @see ClientPhoneLoginPage for phone authentication implementation
 * @see Debug for development login options
 * 
 * @returns {JSX.Element} Rendered login page with phone auth button
 */
export function LoginPage() {
  const { userData } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!userData) return

    if (userData.role === 'trainer') {
      navigate('/trainer', { replace: true })
    } else if (userData.role === 'client') {
      navigate('/client', { replace: true })
    }
  }, [userData, navigate])

  return (
    <>
      <SEOHead
        title="Sign In | Start Grounded"
        description="Sign in to your AI-powered personal training account"
        canonicalUrl="https://startgrounded.com/login"
      />
      <div className="min-h-screen flex items-center justify-center p-4 safe-all" data-testid="login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card variant="elevated" padding="lg" className="shadow-2xl">
            <div className="text-center mb-8">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent"
              >
                Start Grounded
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-gray-600 dark:text-slate-400 text-lg"
              >
                Train smarter, move better
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                onClick={() => navigate('/phone-login')}
                variant="primary"
                size="lg"
                className="w-full"
                data-testid="login-button"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Sign in with phone number
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400"
            >
              <p>
                Are you a trainer?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/trainer-auth')}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                  data-testid="trainer-login-link"
                >
                  Sign in here
                </button>
              </p>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </>
  )
}
